import React from 'react';
import {
  alpha,
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { prepTdSx, prepThSx, shellSx } from '../constants';
import { isPrepBuySignal, signalColor } from '../utils/labels';
import { PrepHistoryRowSkeleton } from './PrepSkeletons';

export default function PreparationHistoryTable({
  t,
  numberLocale,
  historyLoading,
  historyItems,
  historyTone,
  fmtNum,
  fmtPct,
  onOpenHistoryDetail,
}) {
  return (
        <Box sx={{ ...shellSx }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderBottom: '1px solid #eef2f7',
            bgcolor: '#fafbfc',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#0f172a' }}>
                {t('preparation.history.title')}
              </Typography>
              <Tooltip
                arrow
                title={
                  <Box sx={{ p: 0.25, maxWidth: 280 }}>
                    <Typography sx={{ fontSize: '0.72rem', mb: 0.5 }}>{t('preparation.history.panelBody')}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', color: '#cbd5e1' }}>{t('preparation.history.legend.reliabilityBody')}</Typography>
                  </Box>
                }
              >
                <IconButton size="small" sx={{ color: '#94a3b8', p: 0.25 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600 }}>
              {t('preparation.history.lookback')}
            </Typography>
          </Box>

          {historyLoading ? (
            <TableContainer sx={{ p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...prepThSx, width: 108 }}>{t('preparation.history.columns.date')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 72, textAlign: 'right' }}>{t('preparation.history.columns.reliability')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 48, textAlign: 'right' }}>{t('preparation.history.columns.total')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 44, textAlign: 'right' }}>BUY</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 56, textAlign: 'right' }}>{t('preparation.history.columns.avg')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 72, textAlign: 'right' }}>5D</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 72, textAlign: 'right' }}>10D</TableCell>
                    <TableCell sx={prepThSx}>{t('preparation.history.columns.topPicks')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.from({ length: 10 }).map((_, index) => (
                    <PrepHistoryRowSkeleton key={`hist-sk-${index}`} index={index} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : historyItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>{t('preparation.history.empty')}</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...prepThSx, width: 108 }}>{t('preparation.history.columns.date')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 72, textAlign: 'right' }}>{t('preparation.history.columns.reliability')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 48, textAlign: 'right' }}>{t('preparation.history.columns.total')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 44, textAlign: 'right' }}>BUY</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 56, textAlign: 'right' }}>{t('preparation.history.columns.avg')}</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 72, textAlign: 'right' }}>5D</TableCell>
                    <TableCell sx={{ ...prepThSx, width: 72, textAlign: 'right' }}>10D</TableCell>
                    <TableCell sx={prepThSx}>{t('preparation.history.columns.topPicks')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyItems.map((row) => {
                    const tone = historyTone(row.reliability_score || 0);
                    const dateLabel = new Date(row.score_date).toLocaleDateString(numberLocale, { day: '2-digit', month: 'short' });
                    return (
                      <TableRow key={row.score_date} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                        <TableCell sx={{ ...prepTdSx, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>{dateLabel}</TableCell>
                        <TableCell align="right" sx={{ ...prepTdSx, fontWeight: 800, color: tone }}>{fmtNum(row.reliability_score, 1)}</TableCell>
                        <TableCell align="right" sx={prepTdSx}>{row.total_candidates}</TableCell>
                        <TableCell align="right" sx={{ ...prepTdSx, color: '#16a34a', fontWeight: 700 }}>{row.buy_count}</TableCell>
                        <TableCell align="right" sx={prepTdSx}>{fmtNum(row.avg_score, 1)}</TableCell>
                        <TableCell align="right" sx={prepTdSx}>
                          <Typography component="span" sx={{ fontWeight: 700, color: row.avg_fwd_ret_5d >= 0 ? '#16a34a' : '#dc2626', fontSize: 'inherit' }}>
                            {row.avg_fwd_ret_5d != null ? fmtPct(row.avg_fwd_ret_5d) : '—'}
                          </Typography>
                          {row.win_rate_5d != null && (
                            <Typography component="span" sx={{ display: 'block', fontSize: '0.6rem', color: '#94a3b8' }}>
                              {t('preparation.history.winRate', { value: fmtPct(row.win_rate_5d) })}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={prepTdSx}>
                          <Typography component="span" sx={{ fontWeight: 700, color: row.avg_fwd_ret_10d >= 0 ? '#16a34a' : '#dc2626', fontSize: 'inherit' }}>
                            {row.avg_fwd_ret_10d != null ? fmtPct(row.avg_fwd_ret_10d) : '—'}
                          </Typography>
                          {row.win_rate_10d != null ? (
                            <Typography component="span" sx={{ display: 'block', fontSize: '0.6rem', color: '#94a3b8' }}>
                              {t('preparation.history.winRate', { value: fmtPct(row.win_rate_10d) })}
                            </Typography>
                          ) : (
                            <Typography component="span" sx={{ display: 'block', fontSize: '0.6rem', color: '#94a3b8' }}>
                              {t('preparation.history.pending')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...prepTdSx, py: 0.65 }}>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                            {(row.top_picks || []).map((pick) => {
                              const pickTone = isPrepBuySignal(pick.signal) ? signalColor(pick.signal) : '#2563eb';
                              const realized = pick.fwd_ret_10d != null ? pick.fwd_ret_10d : pick.fwd_ret_5d;
                              return (
                                <Box
                                  key={`${row.score_date}-${pick.ticker}`}
                                  onClick={() => onOpenHistoryDetail(pick, row)}
                                  title={t('preparation.history.openDetail')}
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.35,
                                    px: 0.55,
                                    py: 0.15,
                                    borderRadius: 0.75,
                                    border: '1px solid #e2e8f0',
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: pickTone, bgcolor: alpha(pickTone, 0.05) },
                                  }}
                                >
                                  <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: '#0f172a' }}>{pick.ticker}</Typography>
                                  {realized != null && (
                                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: realized >= 0 ? '#16a34a' : '#dc2626' }}>
                                      {fmtPct(realized)}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
  );
}
